/* ==========================================================================
   LAUZON — collection page behaviour

   - FILTER button slides the facet sidebar in beside the grid (prototype
     mechanic, class toggle; CSS animates).
   - Facet changes, sort changes and "Clear all" re-render the grid IN PLACE
     through Shopify's Section Rendering API, then keep the URL in step with
     history.pushState so a filtered view is still linkable and Back/Forward
     work. Added 2026-08-27 — Dave S.: "when you click a brand, it refreshes
     the whole page. Is there any way around that?"
   - Load more fetches the next page and appends its cards.

   🔻 PROGRESSIVE ENHANCEMENT, NOT A REPLACEMENT. Every one of those is a
   layer over markup that already works without it: the facets live in a real
   GET form with a real submit button (`.facet-apply`, hidden only once this
   script marks the form `data-enhanced`), Load more is a real link to
   ?page=N, and every failure path here — a non-200, a parse miss, a thrown
   error — falls through to ordinary navigation rather than leaving the
   visitor on a grid that no longer matches its own controls.

   🔻 OVERLAPPING REQUESTS. A fast clicker fires several. Two guards, because
   either alone is insufficient: an AbortController cancels the request that
   is still out, and a monotonic sequence token discards any response that
   arrives after a newer one was started (abort is not instantaneous, and a
   response already in flight can still resolve). The last click wins.

   Listeners are DELEGATED on the form, which survives every swap; anything
   bound to a node inside the swap boundary would have to be rebound after
   each render and would double up the first time someone forgot.

   Idempotent; re-runs on shopify:section:load for the theme editor.
   ========================================================================== */

(function () {
  'use strict';

  var supported =
    typeof window.fetch === 'function' &&
    typeof window.URLSearchParams === 'function' &&
    typeof window.FormData === 'function' &&
    typeof window.DOMParser === 'function' &&
    !!(window.history && window.history.pushState);

  function init(root) {
    var form = root.querySelector
      ? root.querySelector('#CollectionFilters') || (root.id === 'CollectionFilters' ? root : null)
      : null;
    if (!form) return;

    decorate(form);

    if (form.dataset.lauzonBound) return;
    form.dataset.lauzonBound = '1';

    var sectionId = form.dataset.sectionId;
    var basePath = form.getAttribute('action') || window.location.pathname;
    var status = document.querySelector('[data-filter-status]');
    var seq = 0;
    var controller = null;

    // Enhancing is what hides the no-script submit, so only claim it when the
    // page can actually do the enhanced thing. An old browser keeps the button
    // and the full-page submit, which is the correct outcome, not a fallback.
    if (supported) form.dataset.enhanced = '1';

    /* ---- the URL this form currently describes -------------------------- */
    function formUrl() {
      var out = new URLSearchParams();
      new URLSearchParams(new FormData(form)).forEach(function (value, key) {
        // Empty price inputs would otherwise put `filter.v.price.gte=` in the
        // URL, which Shopify reads as an active filter set to nothing.
        if (value !== '') out.append(key, value);
      });
      var qs = out.toString();
      return basePath + (qs ? '?' + qs : '');
    }

    function sectionUrl(url) {
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
    }

    /* ---- render --------------------------------------------------------- */
    // focusHint: {name, value} of the control that caused this, so focus can
    // land back on it after the swap replaced it with a fresh element. Without
    // it a keyboard user is dumped at the top of the document on every click.
    function render(url, push, focusHint) {
      if (!supported || !sectionId) {
        window.location.href = url;
        return;
      }

      seq += 1;
      var mine = seq;
      if (controller) controller.abort();
      controller = window.AbortController ? new window.AbortController() : null;

      form.classList.add('is-filtering');
      form.setAttribute('aria-busy', 'true');

      fetch(sectionUrl(url), {
        signal: controller ? controller.signal : undefined,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          if (mine !== seq) return; // a newer request has already been started
          var next = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-collection-root]');
          if (!next) throw new Error('section markup not found');

          var body = form.querySelector('[data-collection-body]');
          var wasOpen = !!(body && body.classList.contains('filters-open'));

          // The scroll entrance is for content arriving as you scroll to it.
          // These cards are already where the reader is looking, so they are
          // revealed straight away rather than handed to GSAP — the same call
          // the Load more path has always made.
          next.querySelectorAll('[data-reveal]').forEach(function (el) {
            el.removeAttribute('data-reveal');
          });

          form.innerHTML = next.innerHTML;
          form.classList.remove('is-filtering');
          form.removeAttribute('aria-busy');

          var newBody = form.querySelector('[data-collection-body]');
          if (wasOpen && newBody) {
            newBody.classList.add('filters-open');
            var chip = form.querySelector('.filter-chip[aria-controls]');
            if (chip) chip.setAttribute('aria-expanded', 'true');
          }

          decorate(form);
          if (window.LauzonMotion) {
            var grid = form.querySelector('[data-grid-products]');
            if (grid) window.LauzonMotion.cardHover(grid);
          }

          if (push) window.history.pushState({ lauzonCollection: true }, '', url);

          // Announced from a region OUTSIDE the swap: the node carrying
          // aria-live inside the grid has just been replaced, and a live
          // region that gets replaced announces nothing.
          if (status) {
            var count = form.querySelector('[data-count]') || form.querySelector('.count');
            status.textContent = count ? count.textContent.trim() : '';
          }

          restoreFocus(focusHint);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Anything else — offline, a 5xx, markup that did not parse — hands
          // the visitor to the plain navigation the markup already supported.
          window.location.href = url;
        });
    }

    function restoreFocus(hint) {
      if (!hint) return;
      var el = null;
      if (hint.selector) {
        el = form.querySelector(hint.selector);
      } else if (hint.name) {
        el = form.querySelector(
          '[name="' + cssEscape(hint.name) + '"]' +
            (hint.value ? '[value="' + cssEscape(hint.value) + '"]' : '')
        );
      }
      if (el) {
        el.focus({ preventScroll: true });
      } else if (status) {
        // The control it came from no longer exists (a facet value that is now
        // empty, or Clear all removing its own link). Send focus to the chip
        // that opens the panel rather than to the top of the document.
        var chip = form.querySelector('.filter-chip');
        if (chip) chip.focus({ preventScroll: true });
      }
    }

    function cssEscape(v) {
      return String(v).replace(/["\\]/g, '\\$&');
    }

    /* ---- events, all delegated on the form ------------------------------ */
    form.addEventListener('change', function (e) {
      if (!e.target.matches('[data-autosubmit]')) return;
      if (!supported) {
        form.submit();
        return;
      }
      render(formUrl(), true, {
        name: e.target.name,
        value: e.target.type === 'checkbox' ? e.target.value : ''
      });
    });

    form.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('[data-autosubmit-blur]')) {
        e.preventDefault();
        if (!supported) return form.submit();
        render(formUrl(), true, { name: e.target.name });
      }
    });

    form.addEventListener(
      'blur',
      function (e) {
        if (!e.target.matches || !e.target.matches('[data-autosubmit-blur]')) return;
        if (!e.target.dataset.dirty) return;
        delete e.target.dataset.dirty;
        if (!supported) return form.submit();
        render(formUrl(), true, { name: e.target.name });
      },
      true // blur does not bubble; capture is how one listener covers them all
    );

    form.addEventListener('input', function (e) {
      if (e.target.matches('[data-autosubmit-blur]')) e.target.dataset.dirty = '1';
    });

    form.addEventListener('click', function (e) {
      var chip = e.target.closest('.filter-chip[aria-controls]');
      if (chip) {
        var body = form.querySelector('[data-collection-body]');
        if (!body) return;
        var open = body.classList.toggle('filters-open');
        chip.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }

      var more = e.target.closest('[data-facet-more]');
      if (more) {
        var list = more.parentNode.querySelector('[data-facet-collapse]');
        if (list) list.classList.remove('is-collapsed');
        more.hidden = true;
        return;
      }

      var clear = e.target.closest('.facet-clear, [data-clear-filters]');
      if (clear && supported) {
        e.preventDefault();
        render(clear.getAttribute('href'), true, { selector: '.filter-chip' });
        return;
      }

      var loadMore = e.target.closest('[data-load-more]');
      if (loadMore) {
        e.preventDefault();
        appendNextPage(loadMore);
      }
    });

    /* ---- Back / Forward -------------------------------------------------- */
    // Only same-document entries on this collection reach here; navigating away
    // and back is a real page load, which needs nothing from us.
    window.addEventListener('popstate', function () {
      if (window.location.pathname !== basePath) return;
      render(window.location.pathname + window.location.search, false, null);
    });

    /* ---- Load more ------------------------------------------------------- */
    function appendNextPage(link) {
      var href = link.href;
      link.setAttribute('aria-busy', 'true');
      link.textContent = 'Loading…';
      fetch(supported && sectionId ? sectionUrl(href) : href)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('[data-grid-products]');
          var grid = form.querySelector('[data-grid-products]');
          if (newGrid && grid) {
            Array.prototype.slice.call(newGrid.children).forEach(function (card) {
              card.removeAttribute('data-reveal');
              grid.appendChild(card);
            });
            if (window.LauzonMotion) window.LauzonMotion.cardHover(grid);
          }
          var newCount = doc.querySelector('[data-count]');
          var count = form.querySelector('[data-count]');
          if (newCount && count) {
            var total = grid ? grid.children.length : 0;
            count.textContent = newCount.textContent.replace(/Showing \d+/, 'Showing ' + total);
          }
          var next = doc.querySelector('[data-load-more]');
          if (next) {
            link.href = next.href;
            link.textContent = 'Load more';
            link.removeAttribute('aria-busy');
          } else {
            link.remove();
          }
        })
        .catch(function () {
          window.location.href = href;
        });
    }
  }

  /* ---- per-render decoration -------------------------------------------- */
  // Everything that has to be reapplied to freshly rendered markup. Kept apart
  // from the listeners above, which are bound once and never rebound.
  function decorate(form) {
    form.querySelectorAll('[data-facet-collapse]').forEach(function (list) {
      var btn = list.parentNode.querySelector('[data-facet-more]');
      if (!btn) return;
      // An active value hidden behind the lid would look like the filter
      // cleared itself, so a group with a checked box past the cap opens.
      var hiddenActive = Array.prototype.slice
        .call(list.children)
        .slice(8)
        .some(function (opt) {
          return opt.classList.contains('is-active');
        });
      if (hiddenActive) return;
      list.classList.add('is-collapsed');
      btn.hidden = false;
    });
  }

  init(document);
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
